/**
 * Pure helpers for the Browser Ladder Platform Stats tab — week-bucket
 * math, delta direction, axis labels and small-sample point styling.
 * Unit-tested in series.test.ts; mirrors the bucket shape returned by
 * GET /platform-stats/browser-ladder.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export interface BucketLike {
  /** 'YYYY-MM-DD' — the day itself, or the Monday of the ISO week. */
  bucket: string;
}

export type Granularity = 'daily' | 'weekly';

/**
 * Baseline counts arrive as a per-week mean; on a daily chart they must be
 * read per day. Rates are scale-free and pass through unchanged.
 */
export function baselineForGranularity(
  value: number | null | undefined,
  isCount: boolean,
  granularity: Granularity,
): number | null {
  if (value == null) return null;
  return isCount && granularity === 'daily' ? value / 7 : value;
}

const utcMs = (isoDate: string) => Date.parse(`${isoDate}T00:00:00Z`);

/** A weekly bucket is complete once its full Mon–Sun span is in the past. */
export function weekComplete(bucket: string, now: Date = new Date()): boolean {
  return utcMs(bucket) + 7 * DAY_MS <= now.getTime();
}

/**
 * Index of the most recent COMPLETE week in a weekly series (-1 when the
 * window holds only the current partial week).
 */
export function lastCompleteWeekIndex(
  buckets: readonly BucketLike[],
  now: Date = new Date(),
): number {
  for (let i = buckets.length - 1; i >= 0; i--) {
    if (weekComplete(buckets[i].bucket, now)) return i;
  }
  return -1;
}

export interface Headline<B> {
  /** Last complete week, undefined when the range holds only partial weeks. */
  week?: B;
  prior?: B;
}

/** The last complete week and the one before it, for tiles. */
export function headlineWeeks<B extends BucketLike>(
  weekly: readonly B[],
  now: Date = new Date(),
): Headline<B> {
  const i = lastCompleteWeekIndex(weekly, now);
  return {
    week: i >= 0 ? weekly[i] : undefined,
    prior: i > 0 ? weekly[i - 1] : undefined,
  };
}

/**
 * The in-progress week (last bucket when it is not yet complete), for
 * "this week so far" call-outs next to the complete-week headline.
 */
export function currentWeek<B extends BucketLike>(
  weekly: readonly B[],
  now: Date = new Date(),
): B | undefined {
  const last = weekly[weekly.length - 1];
  return last && !weekComplete(last.bucket, now) ? last : undefined;
}

export interface DeltaTrend {
  label: string;
  direction: 'up' | 'down' | 'flat';
}

const round2 = (v: number) => Math.round(v * 100) / 100;

/**
 * Signed delta vs the previous complete week. `direction` is good/bad, not
 * sign: for lower-is-better metrics a decrease is 'up' (green).
 * Returns undefined when either side is unmeasured.
 */
export function deltaTrend(
  current: number | null,
  previous: number | null,
  lowerIsBetter: boolean,
  suffix = '',
): DeltaTrend | undefined {
  if (current == null || previous == null) return undefined;
  const diff = round2(current - previous);
  if (diff === 0)
    return { label: `±0${suffix} vs prior week`, direction: 'flat' };
  const improved = lowerIsBetter ? diff < 0 : diff > 0;
  return {
    label: `${diff > 0 ? '+' : ''}${diff}${suffix} vs prior week`,
    direction: improved ? 'up' : 'down',
  };
}

/**
 * Signed delta vs the collapsed pre-ladder baseline, same good/bad colouring
 * as `deltaTrend`. Undefined when either side is unmeasured.
 */
export function baselineTrend(
  current: number | null,
  baseline: number | null,
  lowerIsBetter: boolean,
  suffix = '',
): DeltaTrend | undefined {
  if (current == null || baseline == null) return undefined;
  const diff = round2(current - baseline);
  if (diff === 0)
    return { label: `±0${suffix} vs pre-ladder`, direction: 'flat' };
  const improved = lowerIsBetter ? diff < 0 : diff > 0;
  return {
    label: `${diff > 0 ? '+' : ''}${diff}${suffix} vs pre-ladder`,
    direction: improved ? 'up' : 'down',
  };
}

/** Below this many observations a rate is shown but not trended. */
export const MIN_SAMPLE = 10;

export const lowSample = (n: number | undefined): boolean =>
  n == null || n < MIN_SAMPLE;

/** Weekly tile label: '2026-09-08' → 'w/c 8 Sep'. */
export function weekLabel(bucket: string): string {
  const d = new Date(utcMs(bucket));
  return `w/c ${d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })}`;
}

/**
 * X-axis labels for a bucket series. The last bucket is always partial
 * (today in daily view, the current week in weekly view) so it gets a
 * " (partial)" suffix.
 */
export function xLabels(
  buckets: readonly BucketLike[],
  fmt: (bucket: string) => string,
): string[] {
  return buckets.map((b, i) =>
    i === buckets.length - 1 ? `${fmt(b.bucket)} (partial)` : fmt(b.bucket),
  );
}

/**
 * Per-point styling that renders hollow points wherever the bucket's sample
 * (e.g. intents) is below `minN`, so a 3-intent day can't masquerade as a
 * solid trend. Spread onto a dataset; arrays align 1:1 with data points.
 */
export function smallSampleStyle<T extends BucketLike>(
  buckets: readonly T[],
  sampleOf: (b: T) => number,
  minN = 10,
  color = 'rgb(59, 130, 246)',
): { pointBackgroundColor: string[]; pointBorderColor: string[] } {
  return {
    pointBackgroundColor: buckets.map((b) =>
      sampleOf(b) < minN ? 'rgba(0,0,0,0)' : color,
    ),
    pointBorderColor: buckets.map(() => color),
  };
}
